/**
 * Razorpay Checkout.js integration — the frontend widget hand-off, closing
 * the "no real customer can complete a payment through the SPA" P0 gap
 * (order-system-implementation-gap-analysis.md). The backend's Create
 * Checkout response (`keyId`/`gatewayOrderId`/`amountPaise`/`currency`) is
 * everything this module needs — never a client-computed amount.
 *
 * A Razorpay `handler`/success callback is NEVER treated as authoritative
 * proof of payment (order-system-state-machine.md §"Who/what can trigger
 * each transition": "advisory client callback only, never authoritative") —
 * callers of `openRazorpayCheckout` must poll `getPurchase` for the
 * backend-verified webhook outcome, never flip local state to "paid" solely
 * from `onSuccess`.
 */

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, handler: (payload: unknown) => void) => void;
    };
  }
}

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';
const SCRIPT_ATTR = 'data-razorpay-checkout';
const LOAD_TIMEOUT_MS = 15_000;

/** Checkout.js could not be loaded/initialised — the payment window never opened, so nothing was charged. */
export class CheckoutUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CheckoutUnavailableError';
  }
}

let scriptPromise: Promise<void> | null = null;

/** Loads the official Razorpay Checkout.js script exactly once, cached for the page's lifetime. */
export function loadRazorpayScript(): Promise<void> {
  if (typeof window !== 'undefined' && window.Razorpay) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    // Every failure drops the cached promise AND the dead tag: a leftover tag would send the
    // next attempt down the `existing` branch, which can never load anything. A stalled load
    // (captive portal, very slow network) is bounded the same way instead of spinning forever.
    let settled = false; // a late error/poll timeout must not null out a NEWER attempt's cached promise
    const done = () => {
      settled = true;
      resolve();
    };
    const fail = (tag: Element, message: string) => {
      if (settled) return;
      settled = true;
      scriptPromise = null;
      tag.remove();
      reject(new CheckoutUnavailableError(message));
    };
    const existing = document.querySelector(`script[${SCRIPT_ATTR}]`);
    if (existing) {
      // The tag may have already fired `load`/`error` before this call
      // attached its own listeners (e.g. a stale reference surviving a
      // module reload) — a bare `addEventListener` here would then never
      // resolve or reject, hanging the caller forever. Poll for the global
      // it installs as a bounded fallback alongside the listeners. Caught
      // in code review.
      existing.addEventListener('load', done);
      existing.addEventListener('error', () => fail(existing, 'Failed to load Razorpay Checkout.js'));
      const deadline = Date.now() + LOAD_TIMEOUT_MS;
      const poll = () => {
        if (settled) return;
        if (window.Razorpay) return done();
        if (Date.now() >= deadline) return fail(existing, 'Timed out waiting for Razorpay Checkout.js to load');
        setTimeout(poll, 200);
      };
      poll();
      return;
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.setAttribute(SCRIPT_ATTR, 'true');
    const timer = setTimeout(() => fail(script, 'Timed out waiting for Razorpay Checkout.js to load'), LOAD_TIMEOUT_MS);
    script.addEventListener('load', () => {
      clearTimeout(timer);
      done();
    });
    script.addEventListener('error', () => {
      clearTimeout(timer);
      fail(script, 'Failed to load Razorpay Checkout.js');
    });
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface RazorpaySuccessResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface RazorpayFailurePayload {
  code?: string;
  description?: string;
}

export interface OpenRazorpayCheckoutInput {
  keyId: string;
  gatewayOrderId: string;
  /** Smallest currency unit (paise for INR) — the backend's frozen amount, never computed here. */
  amountPaise: number;
  currency: string;
  name?: string;
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  /**
   * Razorpay Customer id. Required for SAVED CARDS: Razorpay only shows a
   * returning buyer their tokenised cards when Checkout is opened with a
   * `customer_id`, which is why the saved-card tab was permanently empty before
   * this existed. Omitted entirely when null — passing `customer_id: undefined`
   * is fine, but passing null is not.
   */
  customerId?: string | null;
  onSuccess: (response: RazorpaySuccessResponse) => void;
  /** The user closed the widget without paying — Purchase stays PAYMENT_PENDING, retryable. */
  onDismiss: () => void;
  onFailure: (error: RazorpayFailurePayload) => void;
}

export async function openRazorpayCheckout(input: OpenRazorpayCheckoutInput): Promise<void> {
  await loadRazorpayScript();
  const Razorpay = window.Razorpay;
  if (!Razorpay) {
    throw new CheckoutUnavailableError('Razorpay Checkout.js did not initialize');
  }

  const instance = new Razorpay({
    key: input.keyId,
    amount: input.amountPaise,
    currency: input.currency,
    order_id: input.gatewayOrderId,
    name: input.name ?? 'InviteCloud',
    description: input.description,
    prefill: input.prefill,
    // Only set when we actually have a customer; Razorpay treats an explicit
    // null as a malformed value, so the key is omitted rather than nulled.
    ...(input.customerId ? { customer_id: input.customerId } : {}),
    // Offers "save this card for next time". Without BOTH this and customer_id
    // there is nothing to save against, so no card is ever tokenised and the
    // saved-card tab stays empty on every future checkout. Tokenisation itself
    // happens entirely on Razorpay's side — no card data reaches this app.
    ...(input.customerId ? { remember_customer: true } : {}),
    handler: (response: unknown) => input.onSuccess(response as RazorpaySuccessResponse),
    modal: { ondismiss: () => input.onDismiss() },
  });
  instance.on('payment.failed', (payload: unknown) => {
    const error = (payload as { error?: RazorpayFailurePayload })?.error ?? {};
    input.onFailure(error);
  });
  instance.open();
}
